'use client';

import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import de from './dictionaries/de';
import en from './dictionaries/en';
import { COOKIE_NAME, DEFAULT_LOCALE, LOCALE_META, STORAGE_KEY, isValidLocale } from './config';

const DICTIONARIES = { de, en };

export const LocaleContext = createContext({
    locale: DEFAULT_LOCALE,
    setLocale: () => {},
    dict: DICTIONARIES[DEFAULT_LOCALE],
    t: (path) => path,
});

function readCookie(name) {
    if (typeof document === 'undefined') return null;
    const match = document.cookie
        .split('; ')
        .find((row) => row.startsWith(`${name}=`));
    return match ? decodeURIComponent(match.split('=')[1]) : null;
}

function writeCookie(name, value) {
    if (typeof document === 'undefined') return;
    const oneYear = 60 * 60 * 24 * 365;
    document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${oneYear}; SameSite=Lax`;
}

function detectInitialLocale() {
    const cookie = readCookie(COOKIE_NAME);
    if (isValidLocale(cookie)) return cookie;
    if (typeof window !== 'undefined') {
        try {
            const stored = window.localStorage.getItem(STORAGE_KEY);
            if (isValidLocale(stored)) return stored;
        } catch {}
        const nav = window.navigator?.language?.toLowerCase().split('-')[0];
        if (isValidLocale(nav)) return nav;
    }
    return DEFAULT_LOCALE;
}

function resolvePath(obj, path) {
    if (!obj || typeof path !== 'string') return undefined;
    return path.split('.').reduce((acc, key) => (acc == null ? acc : acc[key]), obj);
}

export function LocaleProvider({ children }) {
    const [locale, setLocaleState] = useState(DEFAULT_LOCALE);
    const [hydrated, setHydrated] = useState(false);

    useEffect(() => {
        const detected = detectInitialLocale();
        setLocaleState(detected);
        setHydrated(true);
    }, []);

    useEffect(() => {
        if (typeof document === 'undefined') return;
        document.documentElement.lang = LOCALE_META[locale]?.htmlLang ?? locale;
    }, [locale]);

    const setLocale = useCallback((next) => {
        if (!isValidLocale(next)) return;
        setLocaleState(next);
        writeCookie(COOKIE_NAME, next);
        try {
            window.localStorage.setItem(STORAGE_KEY, next);
        } catch {}
    }, []);

    const value = useMemo(() => {
        const dict = DICTIONARIES[locale] ?? DICTIONARIES[DEFAULT_LOCALE];
        const t = (path, fallback) => {
            const value = resolvePath(dict, path);
            if (value === undefined || value === null) return fallback ?? path;
            return value;
        };
        return { locale, setLocale, dict, t, hydrated };
    }, [locale, setLocale, hydrated]);

    return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}
